/**
 * AuthContext - Global authentication state management
 * Uses native Google OAuth for authentication
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

const UNAUTHENTICATED_STATE = {
  user: null,
  isAuthenticated: false,
  usageLimit: null,
  isUnlimited: false,
  consentGiven: false,
  userRole: null,
  betaProcurementCount: null,
  daysRemaining: null,
  procurementsRemaining: null,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [usageLimit, setUsageLimit] = useState(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [betaProcurementCount, setBetaProcurementCount] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [procurementsRemaining, setProcurementsRemaining] = useState(null);

  // Single source of truth for hydrating every auth-derived state field from
  // a server payload. Returns the applied state so a caller that needs the
  // fresh values immediately (e.g. AuthCallback, before its own next render)
  // doesn't have to read them back out of context (which would be stale -
  // setState is not synchronous). Every login path funnels through this via
  // checkAuth() below, so no path can diverge on any field, current or future.
  const applyAuthState = useCallback((state) => {
    setUser(state.user);
    setIsAuthenticated(state.isAuthenticated);
    setUsageLimit(state.usageLimit);
    setIsUnlimited(state.isUnlimited);
    setConsentGiven(state.consentGiven);
    setUserRole(state.userRole);
    setBetaProcurementCount(state.betaProcurementCount);
    setDaysRemaining(state.daysRemaining);
    setProcurementsRemaining(state.procurementsRemaining);
    return state;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // Use /auth/check endpoint to avoid 401 console errors
      const response = await api.checkAuth();
      if (response.data?.authenticated && response.data?.user) {
        return applyAuthState({
          user: response.data.user,
          isAuthenticated: true,
          usageLimit: response.data.usage_limit,
          isUnlimited: response.data.is_unlimited,
          consentGiven: response.data.consent_given || false,
          userRole: response.data.user_role,
          betaProcurementCount: response.data.beta_procurement_count,
          daysRemaining: response.data.days_remaining,
          procurementsRemaining: response.data.procurements_remaining,
        });
      }
      return applyAuthState(UNAUTHENTICATED_STATE);
    } catch (error) {
      // Not authenticated or error
      return applyAuthState(UNAUTHENTICATED_STATE);
    } finally {
      setLoading(false);
    }
  }, [applyAuthState]);

  // Function to update usage limit after an action
  const updateUsageLimit = useCallback((newLimit) => {
    if (newLimit !== undefined && newLimit !== null) {
      setUsageLimit(newLimit);
    }
  }, []);

  // Function to give consent
  const giveConsent = useCallback(async () => {
    try {
      await api.giveConsent();
      setConsentGiven(true);
      return true;
    } catch (error) {
      console.error('Failed to record consent:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from an OAuth callback (the Google OAuth code
    // query param), skip the /me check. AuthCallback will exchange it and
    // establish the session first.
    if (new URLSearchParams(window.location.search).has('code')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // Direct Google OAuth flow - backend redirects to Google, which redirects
    // back to FRONTEND_URL + /auth/google/callback with ?code=&state=.
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.location.href = `${backendUrl}/api/auth/google/login`;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setUsageLimit(null);
      setIsUnlimited(false);
      setConsentGiven(false);
      window.location.href = '/login';
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    usageLimit,
    isUnlimited,
    consentGiven,
    userRole,
    betaProcurementCount,
    daysRemaining,
    procurementsRemaining,
    login,
    logout,
    checkAuth,
    setUser,
    setIsAuthenticated,
    updateUsageLimit,
    giveConsent
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
